import React, { useEffect, useMemo, useRef, useState } from "react";
import { Download, Loader2, Lock, Eye, X, FileCheck, Sparkles, Undo2, Box } from "lucide-react";
import { getFixtureStyle } from "../lib/fixtures";
import { findInteriorDoorPairs, computeWindows, DOOR_RADIUS_M, findWallAtPoint } from "../lib/openings";
import { computeWallSegments, buildWallTypeSchedule, MATERIAL_CATALOG } from "../lib/walls";
import { getSymbol } from "../lib/symbols";
import { snap as snapToGrid, fmtSize } from "../lib/units";
import WallTypeLegend from "./WallTypeLegend";
import HatchDefs from "./HatchDefs";
import SymbolPalette from "./SymbolPalette";
import OpeningEditor from "./OpeningEditor";
import OpeningOverlayLayer from "./OpeningOverlayLayer";
import SymbolLayer from "./SymbolLayer";
import WallAddMenu from "./WallAddMenu";
import WallViolationLayer from "./WallViolationLayer";
import WallHandleLayer from "./WallHandleLayer";
import ProjectUnlockPaywall from "./ProjectUnlockPaywall";

// V2.0.44 — P1 Phase 1 feature flag. Set to false to disable the
// shared-wall drag handles without touching any rendering logic.
const WALL_HANDLES_ENABLED = true;
import { fmtSqft, fmtFtIn, buildWallRecommendation } from "../lib/floorplan2dHelpers";
import useDraggable from "../hooks/useDraggable";
import useContainerSize from "../hooks/floorplan2d/useContainerSize";
import useTouchZoom from "../hooks/floorplan2d/useTouchZoom";
import useUndoHistory from "../hooks/floorplan2d/useUndoHistory";
import useFloorPlanShortcuts from "../hooks/floorplan2d/useFloorPlanShortcuts";
import useWallDrawTool from "../hooks/floorplan2d/useWallDrawTool";
import useOpeningDragMode from "../hooks/floorplan2d/useOpeningDragMode";
import useWallAddMenu from "../hooks/floorplan2d/useWallAddMenu";
import useSymbolDrag from "../hooks/floorplan2d/useSymbolDrag";
import useCanvasPointer from "../hooks/floorplan2d/useCanvasPointer";
import { emitInteractionEvent } from "../lib/interactionTelemetry";
import ExportLightbox from "./ExportLightbox";
import UserWallsLayer from "./UserWallsLayer";
import useExportActions from "../hooks/floorplan2d/useExportActions";
import TosConsentCheckbox from "./TosConsentCheckbox";
import { toast } from "sonner";

/**
 * V1.7 — 2D Floor Plan view. The "blueprint deliverable" the user actually
 * wants to walk away with. Reads the Simple-Mode blueprint schema (rooms
 * with x/z/width/depth/name/area_sqft) and renders a clean top-down SVG.
 *
 * V1.7.5 — Auto-fits to the live container size (ResizeObserver) so the
 * plan fills its frame on every viewport, instead of being pinned to a
 * static 720-px max-width that left half the canvas blank on wide screens.
 */
const PADDING = 48;            // SVG canvas padding in px
const MIN_SCALE = 16;          // px per metre at minimum
const STROKE_EXTERIOR = 3;
const STROKE_INTERIOR = 1.5;

export default function FloorPlan2D({ blueprint, floorIndex = 1, className = "", designId, onCheckout, onUpdateBlueprint, onPreviewBlueprint, onChangeFloor, measurementSystem = "imperial", violations = [] }) {
  const [activeFloor, setActiveFloor] = useState(floorIndex);
  // V1.9.9 — keep the internal `activeFloor` (used by the bounds calc +
  // in-canvas switcher) in sync with the controlled `floorIndex` prop
  // from the parent. Without this, the SVG renderer rendered floor N
  // while ALL persisting writes (symbols, openings, keyboard) went to
  // floor floorIndex — manifesting as "1st-floor orientation off vs 3D".
  useEffect(() => { setActiveFloor(floorIndex); }, [floorIndex]);
  // V2.0.18 — PDF + IFC export lightbox state machine extracted to
  // `useExportActions` for unit testability + clarity.
  const {
    pdfState, setPdfState,
    previewDataUrl,
    handlePreviewPDF, handleDownloadCleanPDF, handleDownloadIFC, closeLightbox: _closeLightboxRaw,
  } = useExportActions(designId);
  // V2.0.19 → V2.0.20 — Required legal-disclaimer acceptance for paid
  // downloads. The TosConsentCheckbox component now POSTs to
  // /api/tos/accept and surfaces an audit-row id; we keep the id in
  // state so the download buttons can disable until both the checkbox
  // is ticked AND the audit row has been recorded.
  const [legalAcceptanceId, setLegalAcceptanceId] = useState(null);
  const legalAccepted = !!legalAcceptanceId;
  const closeLightbox = () => {
    setLegalAcceptanceId(null);
    _closeLightboxRaw();
  };
  // V1.7.6 — Plan-canvas Compare Modes toggle. "diagram" = current
  // friendly view; "architectural" = same SVG but styled to match the
  // paid PDF (thick walls + feet-inches dimensions + door arcs). Free to
  // toggle — the value pitch is "see what you're buying".
  const [planMode, setPlanMode] = useState("diagram");
  // V1.7.5 — live container size for auto-fitting the SVG to whatever
  // frame the parent gives us. (V2.0.18: extracted to `useContainerSize`.)
  const [containerRef, containerSize] = useContainerSize({ w: 1100, h: 700 });
  // V1.8.5 — hovering a Material Legend row highlights matching fixtures.
  const [hoveredFixtureType, setHoveredFixtureType] = useState(null);

  // V1.9.1 — Make the Preview PDF button draggable. The default origin
  // stays exactly where it was before (top-14 / center-x) — the hook
  // just adds an x/y delta on top, so users who don't touch it see no
  // change.
  const previewPdfDrag = useDraggable("preview-pdf-btn", { x: 0, y: 0 });

  // V1.9.2 — Symbol Palette: drag/drop, select, move, rotate, delete.
  // `symbols` live on floor.symbols = [{ id, type, x, z, rotation_deg }]
  // x/z are metres (world coords). Rotation is degrees clockwise.
  const [selectedSymbolId, setSelectedSymbolId] = useState(null);
  // V1.9.3 — Tracks whether a pointer interaction crossed the drag
  // threshold. Used to suppress the click event after drag-along-wall.
  const didDragRef = useRef(false);
  // V1.9.2 — Opening editor state. When the user clicks a door/window,
  // we capture (kind, idx, type, anchor) and show OpeningEditor — a tiny
  // popover with type chips + delete button. Mutations bubble up via
  // onUpdateBlueprint (same channel as the symbols flow).
  const [openingEditor, setOpeningEditor] = useState(null);
  // V2.0.82 — Symbol drag state atoms + persistSymbols consolidated
  // into `useSymbolDrag`. Access via `symbolDrag.dragSymbol` /
  // `.moveSymbol` / `.resizeOp` etc. Refs are exposed on the same
  // returned object.
  const symbolDrag = useSymbolDrag({
    blueprint,
    floorIndex,
    onUpdateBlueprint,
    onPreviewBlueprint,
    pushHistory: () => pushHistory(),
  });
  const {
    dragSymbol, setDragSymbol,
    moveSymbol, setMoveSymbol,
    resizeOp, setResizeOp,
    dragInFlightRef, pendingBlueprintRef,
    persistSymbols,
    handleResizeStart: handleResizeStartFromHook,
  } = symbolDrag;
  // V1.9.3 — Drag-along-wall state for AI-placed openings.
  const [openingDrag, setOpeningDrag] = useState(null); // { kind, idx, axis, coord, span_lo, span_hi, origCentre, origPos }
  const svgRef = useRef(null);

  // V2.0.0 — Undo history. (V2.0.18: extracted to `useUndoHistory`.)
  const { pushHistory, undo, popHistory } = useUndoHistory(blueprint, onUpdateBlueprint);

  // V2.0.0 — Click-vs-pointerdown materialise cache. handleOpeningPointerDown
  // ALREADY materialises an auto-inferred opening so the drag can persist
  // its position. The subsequent click event would re-enter materialise
  // and create a SECOND explicit row. We cache the first materialise here
  // and re-use it in handleOpeningClick.
  const lastMaterialisedRef = useRef(null); // { kind, sourceKey, idx }
  const openingSourceKey = (kind, source) => {
    if (kind === "door") {
      return `door|${source._autoRoomA || ""}|${source._autoRoomB || ""}|${source._explicitIdx ?? -1}|${source.axis}|${source.coord?.toFixed?.(3)}`;
    }
    return `window|${source._autoRoom || ""}|${source._autoSide || ""}|${source._explicitIdx ?? -1}|${source.axis}|${source.coord?.toFixed?.(3)}|${source.centre?.toFixed?.(3)}`;
  };

  // V2.0.5 — Pinch-zoom + 2-finger pan on touch devices. (V2.0.18:
  // extracted to `useTouchZoom`.) Single-finger touches pass straight
  // through to the existing pointer handlers.
  const {
    touchZoom,
    onTouchStart: handleSvgTouchStart,
    onTouchMove: handleSvgTouchMove,
    onTouchEnd: handleSvgTouchEnd,
    onWheel: handleSvgWheel,
    onMouseDownPan: handleSvgMouseDownPan,
    reset: resetTouchZoom,
  } = useTouchZoom();

  // V2.0.82 — persistSymbols consolidated into `useSymbolDrag`.
  // See top of file where the hook is instantiated.

  // V1.9.2 — Quick accessor. `floor` is declared lower in this file
  // (after a useMemo), so we can't take a top-level const here without
  // tripping TDZ. The getter is read at call time — by then `floor` is
  // initialised on every code path that actually uses these handlers
  // (drop, click, keyboard).
  const getCurrentSymbols = () => {
    const f = (blueprint?.floors || [])[floorIndex - 1];
    return Array.isArray(f?.symbols) ? f.symbols : [];
  };

  // V1.9.2 — Opening editor mutators. Doors/windows on this floor are
  // either auto-inferred by the AI (no explicit ID) or manually placed
  // (explicit ID). Manual ones we can edit directly. Auto-inferred ones
  // we materialise into an explicit `doors`/`windows` entry on first
  // user interaction, then mutate from there.
  const persistOpenings = (key, list) => {
    if (!blueprint) return;
    // V2.0.0 — see persistSymbols. Drag-start pushes once; we skip
    // pushing per preview frame.
    if (!dragInFlightRef.current) pushHistory();
    // V2.0.1 — base the next blueprint on the most recent IN-FLIGHT
    // preview if one exists, so back-to-back preview writes (e.g.,
    // materialise → drag-along-wall) don't drop the materialise.
    const base = (dragInFlightRef.current && pendingBlueprintRef.current) || blueprint;
    const next = JSON.parse(JSON.stringify(base));
    const f = (next.floors || [])[floorIndex - 1];
    if (!f) return;
    f[key] = list;
    const handler = dragInFlightRef.current && onPreviewBlueprint
      ? onPreviewBlueprint
      : onUpdateBlueprint;
    if (dragInFlightRef.current) pendingBlueprintRef.current = next;
    handler?.(next);
  };

  // V1.9.5 — Materialise an auto-inferred opening into an explicit
  // floor.doors / floor.windows row so it becomes editable + draggable.
  // Returns { list, idx } where list is the updated array (already
  // persisted) and idx is the new row's index. For openings that are
  // ALREADY explicit, this is a cheap no-op pass-through.
  const materializeIfNeeded = (kind, source) => {
    const f = (blueprint?.floors || [])[floorIndex - 1];
    const key = kind === "door" ? "doors" : "windows";
    const list = Array.isArray(f?.[key]) ? [...f[key]] : [];
    if (source._explicitIdx != null && source._explicitIdx >= 0) {
      return { list, idx: source._explicitIdx, materialised: false };
    }
    if (kind === "door") {
      if (!source._autoRoomA || !source._autoRoomB) {
        return { list, idx: -1, materialised: false };
      }
      // V1.9.5 — pre-fill position so an immediate reload shows the door
      // at the inferred centre (or the explicit `position` value if the
      // pair already carried one). Matches the windows-branch convention.
      const defaultPos = Number.isFinite(source.position) ? source.position : 0.5;
      const newRow = {
        id: source.id || `D${list.length + 1}`,
        room_a: source._autoRoomA,
        room_b: source._autoRoomB,
        type: source.type || "swing",
        position: defaultPos,
        // V1.9.9 — flag so deleteOpening knows to TOMBSTONE this row
        // instead of just splicing it (which would let the auto-inference
        // bring it right back at its default location).
        _wasAutoInferred: true,
      };
      list.push(newRow);
    } else {
      if (!source._autoRoom || !source._autoSide) {
        return { list, idx: -1, materialised: false };
      }
      const newRow = {
        id: source.id || `W${list.length + 1}`,
        room: source._autoRoom,
        side: source._autoSide,
        position: Number.isFinite(source._autoPosition) ? source._autoPosition : 0.5,
        width: Number.isFinite(source.len) && source.len > 0 ? source.len : 1.2,
        type: source.type || "fixed",
        // V1.9.9 — see materialise-door branch.
        _wasAutoInferred: true,
      };
      list.push(newRow);
    }
    persistOpenings(key, list);
    return { list, idx: list.length - 1, materialised: true };
  };

  const handleOpeningClick = (kind, e, source) => {
    e.stopPropagation();
    // V2.0.0 — If pointerdown just materialised this same opening, reuse
    // its idx and re-read the FRESH list from blueprint (pointerup just
    // flushed the materialise to the parent). Without this we'd double-
    // materialise the same opening AND the editor would target the wrong
    // index after the list grew.
    const cached = lastMaterialisedRef.current;
    const sourceKey = openingSourceKey(kind, source);
    let list, idx;
    if (cached && cached.kind === kind && cached.sourceKey === sourceKey) {
      const key = kind === "door" ? "doors" : "windows";
      // V2.0.1 — prefer the cached list (which definitely includes the
      // materialised row) over the current blueprint prop, which may
      // still be lagging by a render. Falls back to blueprint if the
      // cached list happens to be missing.
      if (Array.isArray(cached.list) && cached.idx < cached.list.length) {
        list = [...cached.list];
        idx = cached.idx;
      } else {
        const f = (blueprint?.floors || [])[floorIndex - 1];
        list = Array.isArray(f?.[key]) ? [...f[key]] : [];
        idx = cached.idx;
      }
    } else {
      ({ list, idx } = materializeIfNeeded(kind, source));
    }
    // Clear the cache — next click should start fresh.
    lastMaterialisedRef.current = null;
    setOpeningEditor({
      kind,
      idx,
      list,
      type: source.type || (kind === "door" ? "swing" : "fixed"),
      // V2.0.1 — Seed hinge + swing direction from the explicit row (if
      // present in the materialised list) so the OpeningEditor's hinged-
      // door toggles reflect the current state. Defaults to "auto".
      hinge: list[idx]?.hinge || "auto",
      swing_direction: list[idx]?.swing_direction || "auto",
      anchorX: e.clientX,
      anchorY: e.clientY,
    });
  };

  const applyOpeningType = (newType) => {
    if (!openingEditor) return;
    const key = openingEditor.kind === "door" ? "doors" : "windows";
    const list = [...openingEditor.list];
    if (openingEditor.idx >= 0 && openingEditor.idx < list.length) {
      list[openingEditor.idx] = { ...list[openingEditor.idx], type: newType };
      persistOpenings(key, list);
      setOpeningEditor({ ...openingEditor, type: newType, list });
    }
  };

  // V2.0.1 — Generic mutator for non-type opening fields. Used by the
  // hinge + swing-direction toggles on the OpeningEditor popover. We
  // route through persistOpenings so the undo-history stack records
  // each toggle as its own step.
  const applyOpeningField = (field, value) => {
    if (!openingEditor) return;
    const key = openingEditor.kind === "door" ? "doors" : "windows";
    const list = [...openingEditor.list];
    if (openingEditor.idx >= 0 && openingEditor.idx < list.length) {
      list[openingEditor.idx] = { ...list[openingEditor.idx], [field]: value };
      persistOpenings(key, list);
      setOpeningEditor({ ...openingEditor, [field]: value, list });
    }
  };

  const deleteOpening = () => {
    if (!openingEditor) return;
    const key = openingEditor.kind === "door" ? "doors" : "windows";
    // V2.0.24 — ALWAYS tombstone on delete (was: splice for AI-placed
    // explicit rows, tombstone for materialised auto-inferred ones).
    // The old splice branch let the AI re-emit the same door in its next
    // patch because there was no tombstone to block it — manifesting as
    // "I deleted that door but it came back after AI made a change."
    // Tombstoning unconditionally means: delete is permanent until the
    // user explicitly re-adds the opening.
    //
    // V2.0.26 — Stamp the tombstone with `deleted_at` so the Recently
    // Deleted dialog can sort newest-first and surface a relative
    // timestamp ("2 minutes ago", "yesterday"). The backend's
    // `reapply_user_tombstones` deep-copies tombstones across AI
    // roundtrips so this field survives untouched.
    //
    // We DO still skip the tombstone for purely client-side rows that
    // were never persisted (e.g. mid-drag preview openings) — those have
    // no stable id yet and can be safely spliced.
    const idx = openingEditor.idx;
    if (idx < 0 || idx >= openingEditor.list.length) {
      setOpeningEditor(null);
      return;
    }
    const target = openingEditor.list[idx];
    const isUnpersistedPreview = !!target?._previewOnly;
    let newList;
    if (isUnpersistedPreview) {
      newList = openingEditor.list.filter((_, i) => i !== idx);
    } else {
      const now = new Date().toISOString();
      newList = openingEditor.list.map((row, i) =>
        i === idx ? { ...row, deleted: true, deleted_at: now } : row,
      );
    }
    persistOpenings(key, newList);
    setOpeningEditor(null);
  };

  // Convert pointer event clientX/clientY → world metres on the canvas.
  const pointerToWorld = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    // SVG viewBox = svgW × svgH ; visible rect may be scaled by CSS.
    const xPx = ((e.clientX - rect.left) / rect.width) * svgW;
    const yPx = ((e.clientY - rect.top) / rect.height) * svgH;
    // Inverse of toX/toY:
    const xM = bounds.minX + (xPx - PADDING) / scale;
    const zM = bounds.maxZ - (yPx - PADDING) / scale;
    return { x_m: xM, z_m: zM };
  };

  // V2.0.78 — Wall-drawing tool extracted to `useWallDrawTool`. Own
  // state + click flow live in the hook; this component only wires
  // the returned callbacks to the SVG event handlers and renders the
  // preview + toolbar button.
  const wallTool = useWallDrawTool({
    blueprint,
    floorIndex,
    measurementSystem,
    pointerToWorld,
    onUpdateBlueprint,
    pushHistory,
    onActivate: () => setSelectedSymbolId(null),
  });

  // V2.0.80 — "Just-added door/window drag positioning" state machine
  // extracted to `useOpeningDragMode`. Owns the {kind, idx, axis,
  // span_lo, span_hi, coord, room_id/a/b} config plus the pointer-move
  // clamp + preview push. The parent still owns the click-commits and
  // WallAddMenu insertion; this hook just handles what happens between
  // the two. Named `placeDrag` to disambiguate from `openingDrag` which
  // is the drag-EXISTING-opening flow (different feature).
  const placeDrag = useOpeningDragMode({
    blueprint,
    floorIndex,
    pointerToWorld,
    onPreviewBlueprint,
  });

  // V2.0.80 — Wall-add popover state + Add/Merge handlers extracted to
  // `useWallAddMenu`. Owns menu open/close plus the door/window insert
  // and interior-wall room-merge flows. The parent's click detection
  // (`handleSvgClick`) still decides WHEN to open the menu; this hook
  // owns everything the menu does after that.
  const wallAddMenu = useWallAddMenu({
    blueprint,
    floorIndex,
    pushHistory,
    onUpdateBlueprint,
    placeDrag,
  });

  const handleSvgDragOver = (e) => {
    if (e.dataTransfer.types.includes("application/x-blueprint-symbol")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleSvgDrop = (e) => {
    const type = e.dataTransfer.getData("application/x-blueprint-symbol");
    if (!type) return;
    e.preventDefault();
    const world = pointerToWorld(e);
    if (!world) return;
    const sym = getSymbol(type);
    if (!sym) return;
    // Snap to 0.25m grid for clean placement.
    const snap = (v) => snapToGrid(v, measurementSystem);
    // Place the CENTER of the symbol at the drop point.
    const newSymbol = {
      id: `sym_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      x: snap(world.x_m - sym.width_m / 2),
      z: snap(world.z_m - sym.depth_m / 2),
      rotation_deg: 0,
    };
    persistSymbols([...getCurrentSymbols(), newSymbol]);
    setSelectedSymbolId(newSymbol.id);
    setDragSymbol(null);
  };

  // Pointer-down on a placed symbol → start move.
  const handleSymbolPointerDown = (e, sym) => {
    e.stopPropagation();
    setSelectedSymbolId(sym.id);
    const world = pointerToWorld(e);
    if (!world) return;
    // V2.0.0 — snapshot for undo BEFORE marking the drag in-flight so
    // the subsequent per-frame persistSymbols calls don't push more.
    pushHistory();
    dragInFlightRef.current = true;
    setMoveSymbol({
      id: sym.id,
      offsetX: world.x_m - sym.x,
      offsetZ: world.z_m - sym.z,
    });
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* synthetic events */ }
  };

  // V1.9.3 — Pointer-down on a corner handle → start resize.
  // V2.0.86: extracted to `useSymbolDrag.handleResizeStart`. This
  // thin wrapper feeds it the world+catalog lookups it needs at call time.
  const handleResizeStart = (e, sym, corner) =>
    handleResizeStartFromHook(e, sym, corner, { pointerToWorld, getSymbol });

  // V1.9.3/V1.9.4/V1.9.5 — Pointer-down on an opening overlay → start
  // drag-along-wall. For auto-inferred openings we materialise on first
  // interaction so the drag has a persistable target.
  const handleOpeningPointerDown = (e, kind, source) => {
    e.stopPropagation();
    // V2.0.0 — snapshot for undo BEFORE materialise (which writes the
    // first persistOpenings) and BEFORE setting dragInFlightRef so the
    // single snapshot captures the pre-interaction state.
    pushHistory();
    dragInFlightRef.current = true;
    const { list, idx, materialised } = materializeIfNeeded(kind, source);
    if (idx < 0) {
      // Auto-inferred source missing identity tags — bail without
      // entering drag state. Pop the snapshot we just pushed so undo
      // doesn't surface a no-op step.
      dragInFlightRef.current = false;
      popHistory();
      return;
    }
    // V2.0.0 — record what we materialised so the immediately-following
    // click event (same pointer interaction) can reuse the idx instead
    // of double-materialising the same opening.
    // V2.0.1 — also stash the materialised LIST snapshot so the click
    // handler can render the editor even if the parent React state
    // hasn't propagated the materialise to the blueprint prop yet.
    lastMaterialisedRef.current = {
      kind,
      sourceKey: openingSourceKey(kind, source),
      idx,
      list,
      materialised,
    };
    setOpeningDrag({
      kind,
      idx,
      axis: source.axis,
      coord: source.coord,
      span_lo: source.span_lo ?? (source.axis === "x" ? source.centre - source.len / 2 : source.centre - source.len / 2),
      span_hi: source.span_hi ?? (source.axis === "x" ? source.centre + source.len / 2 : source.centre + source.len / 2),
      halfLen_m: ((source.len || (source.span_hi - source.span_lo)) || 1) / 2,
      list,
    });
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* synthetic events */ }
  };

  // V2.0.83 — Canvas pointer orchestration is set up further down,
  // once `rooms` (from the floor useMemo) and `hoverWall` state are
  // in scope. See just after the `useMemo(() => rooms, fixtures,
  // bounds)` block.

  // Click on empty canvas → clear selection. V2.0.7: if the click is
  // within ~0.5m of a wall AND no symbol was selected, OPEN the
  // wall-add-opening menu instead. This is the desktop "click to add"
  // workflow the user expects from architectural CAD tools — clicking
  // an exterior wall offers Window/Door, clicking an interior wall
  // jumps to the interior-door type picker. (V2.0.80: menu state +
  // handlers extracted to `useWallAddMenu`; access via `wallAddMenu.menu`
  // / `.open({...})` / `.close()` / `.handleAddPick` / `.handleMerge`.)

  // V2.0.8 — Hover preview: the wall segment closest to the cursor. We
  // cache it on every pointermove (when no drag/menu is active) and
  // render a cyan highlight over the wall so the user can see WHERE
  // their click will land.
  const [hoverWall, setHoverWall] = useState(null);
  // V2.0.8 — Place-by-drag state now lives in `useOpeningDragMode`
  // (extracted V2.0.80). Access via `placeDrag.active / .mode / .setMode`.

  // V2.0.8 — Build the recommendation for the open menu. Memoised
  // locally on the wall ref so we don't recompute on every render.
  const buildRecommendation = (wall) => buildWallRecommendation(wall, rooms);

  const handleSvgClick = (e) => {
    // V2.0.77 — Wall drawing tool intercepts clicks BEFORE any other
    // canvas interaction so we don't accidentally trigger the opening
    // menu while placing walls.
    if (wallTool.active) {
      wallTool.handleClick(e);
      return;
    }
    // V2.0.8 — if we're in place-by-drag mode, the click commits the
    // current position and exits the mode. The opening row was already
    // persisted by handleWallAddPick — the drag was just preview.
    if (placeDrag.handleClick()) {
      return;
    }
    if (selectedSymbolId) { setSelectedSymbolId(null); return; }
    const world = pointerToWorld(e);
    if (!world) return;
    const hit = findWallAtPoint(rooms || [], world.x_m, world.z_m, 0.5);
    if (!hit) return;
    // V2.0.10 — Build merge option for interior walls. Resolves the two
    // adjacent room names so the menu can show "Merge Living + Kitchen".
    let mergeOption = null;
    if (hit.kind === "interior" && hit.room_a && hit.room_b) {
      const ra = (rooms || []).find((r) => r.id === hit.room_a);
      const rb = (rooms || []).find((r) => r.id === hit.room_b);
      if (ra && rb) {
        mergeOption = {
          roomAName: (ra.name || "Room").trim(),
          roomBName: (rb.name || "Room").trim(),
        };
      }
    }
    wallAddMenu.open({ wall: hit, anchorX: e.clientX, anchorY: e.clientY, recommendation: buildRecommendation(hit), mergeOption });
  };

  // V2.0.10 — One-click "Merge into open plan". Adds room_b's id to the
  // V2.0.10 — Merge Rooms via wall click. Clicking an interior wall
  // now offers a "Merge Rooms" option in the popover; picking it adds
  // the opposite room's id to the first room's `merged_with` array so
  // the two spaces read as one open-plan zone.
  // (V2.0.80: extracted to `useWallAddMenu`.)

  // V2.0.8 — Insert the user's wall-add-menu pick into floor.doors[] or
  // floor.windows[], then enter drag-to-position mode.
  // (V2.0.80: extracted to `useWallAddMenu`.)

  // Keyboard: arrows rotate by 5° (tap) or 15° (shift). Delete removes.
  // Escape deselects. (V2.0.18: extracted to `useFloorPlanShortcuts`.)
  useFloorPlanShortcuts({
    floorIndex,
    blueprint,
    selectedSymbolId,
    placeDragMode: placeDrag.mode,
    setPlaceDragMode: placeDrag.setMode,
    setSelectedSymbolId,
    persistSymbols,
    undo,
    onUpdateBlueprint,
  });

  // V2.0.77 — Wall tool: Escape exits the tool (or clears an in-flight
  // start point without exiting so the user can retry the segment).
  // Extracted to `useWallDrawTool` in V2.0.78.

  const floors = (blueprint?.floors || []).filter((f) => f && Array.isArray(f.rooms));

  // V1.7.6 — Preview-first flow. Step 1: free watermarked preview opens
  // in a lightbox. Step 2: user clicks "Download Clean PDF · 1 credit"
  // inside the lightbox to spend the credit. (V2.0.18: PDF + IFC actions
  // extracted to `useExportActions`; this comment is preserved for
  // archival context.)
  const floor = useMemo(
    () =>
      floors.find((f) => Number(f.floor_index ?? f.level) === Number(activeFloor)) || floors[0],
    [floors, activeFloor],
  );

  const { rooms, fixtures, bounds } = useMemo(() => {
    const rs = (floor?.rooms || []).filter(
      (r) => r && Number.isFinite(r.x) && Number.isFinite(r.z) && r.width > 0 && r.depth > 0,
    );
    // V1.8.3 — Fixtures (appliances + built-in cabinetry). Loose schematic
    // representation only; the paid Architectural PDF gets exact measurements.
    const fxs = (floor?.fixtures || []).filter(
      (f) =>
        f &&
        Number.isFinite(f.x) &&
        Number.isFinite(f.z) &&
        Number(f.width) > 0 &&
        Number(f.depth) > 0,
    );
    if (rs.length === 0) {
      return { rooms: [], fixtures: [], bounds: { minX: 0, minZ: 0, maxX: 10, maxZ: 10 } };
    }
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    rs.forEach((r) => {
      minX = Math.min(minX, r.x);
      minZ = Math.min(minZ, r.z);
      maxX = Math.max(maxX, r.x + r.width);
      maxZ = Math.max(maxZ, r.z + r.depth);
    });
    return { rooms: rs, fixtures: fxs, bounds: { minX, minZ, maxX, maxZ } };
  }, [floor]);

  // V1.8.6 — Compute door arcs + window centres in WORLD metres. Pixel
  // conversion happens at render time using the current scale+toX/toY.
  // V1.8.7 — Honor AI-emitted `doors`/`windows` arrays on the floor when
  // present. Each room can have a hint like `doors: [{room_a, room_b, type}]`
  // and `windows: [{room, side, position, width, type}]`.
  const { doorPairs, exteriorWindows } = useMemo(() => {
    if (rooms.length === 0) return { doorPairs: [], exteriorWindows: [] };
    const explicitDoors = Array.isArray(floor?.doors) ? floor.doors : [];
    const explicitWindows = Array.isArray(floor?.windows) ? floor.windows : [];
    const pairs = findInteriorDoorPairs(rooms, explicitDoors);
    const wins = computeWindows(rooms, explicitWindows);
    return { doorPairs: pairs, exteriorWindows: wins };
  }, [rooms, floor]);

  // V2.0.83 — Canvas pointer orchestrator. Placed here so `rooms`
  // (from the floor useMemo above) and every other dependency is in
  // scope. Returns `handlePointerMove` + `handlePointerUp` wired to
  // the SVG in the JSX below.
  const canvasPointer = useCanvasPointer({
    blueprint,
    rooms,
    measurementSystem,
    pointerToWorld,
    wallTool,
    placeDrag,
    wallAddMenu,
    symbolDrag,
    hoverWall,
    setHoverWall,
    openingDrag,
    setOpeningDrag,
    getCurrentSymbols,
    persistOpenings,
    onUpdateBlueprint,
  });
  const handleSvgPointerMove = canvasPointer.handlePointerMove;
  const handleSvgPointerUp = canvasPointer.handlePointerUp;

  // V1.8.8 — Compute wall segments + the Wall Type Schedule. Honors the
  // optional `wall_types` config on the floor — exterior + interior default
  // material, plus per-pair segment overrides for acoustic/load-bearing walls.
  const { wallSegments, wallSchedule } = useMemo(() => {
    if (rooms.length === 0) return { wallSegments: [], wallSchedule: [] };
    const segs = computeWallSegments(rooms, floor?.wall_types);
    return { wallSegments: segs, wallSchedule: buildWallTypeSchedule(segs) };
  }, [rooms, floor]);

  if (!floor || rooms.length === 0) {
    return (
      <div
        data-testid="floor-plan-2d-empty"
        className={`grid place-items-center h-full text-zinc-500 font-mono text-xs ${className}`}
      >
        <div className="text-center max-w-sm">
          <div className="text-brass-400 mb-2 tracking-widest text-[10px]">// FLOOR PLAN</div>
          <p>Describe your home in plain English and the floor plan will draw itself.</p>
        </div>
      </div>
    );
  }

  // Auto-fit: pick the largest scale that fits within the live container,
  // leaving room for padding + the title compass + the download button row.
  const widthM = bounds.maxX - bounds.minX;
  const depthM = bounds.maxZ - bounds.minZ;
  const reservedH = 80;          // top button row + bottom breathing room
  const availW = Math.max(400, containerSize.w - PADDING * 2);
  const availH = Math.max(300, containerSize.h - PADDING * 2 - reservedH);
  const scaleX = availW / widthM;
  const scaleZ = availH / depthM;
  const scale = Math.max(MIN_SCALE, Math.min(scaleX, scaleZ));
  const innerW = widthM * scale;
  const innerH = depthM * scale;
  const svgW = innerW + PADDING * 2;
  const svgH = innerH + PADDING * 2;

  const toX = (m) => PADDING + (m - bounds.minX) * scale;
  // V1.7 — Flip Y so the front of the house (low z, public rooms) renders
  // at the BOTTOM of the SVG and the back of the house (high z, bedrooms)
  // at the TOP. Matches the convention users expect from real architectural
  // floor plans where the entry/foyer is "down" / toward the reader.
  const toY = (m) => PADDING + (bounds.maxZ - m) * scale;


  // V1.8.6 — Convert door pairs + exterior windows (world meters) into the
  // SVG pixel-space draw lists used in the canvas below. V1.8.7 forwards
  // the door/window TYPE + ID through to the renderer.
  // V2.0.1 — Honor explicit `hinge` (auto|left|right) and
  // `swing_direction` (auto|inswing|outswing) overrides:
  //   - `hinge`: which end of the doorway carries the pivot. Default
  //     (auto/left) = low end of the span (low-Z for axis="z" doors,
  //     low-X for axis="x" doors); "right" flips to the HIGH end.
  //   - `swing_direction`: which side of the wall the arc swings into.
  //     Default (auto) follows the bigger-room heuristic. "inswing"
  //     forces toward the HIGH side of the wall axis (HIGH X for
  //     axis="z" doors / HIGH Z for axis="x" doors); "outswing" forces
  //     the LOW side. The labels are intentionally generic for interior
  //     doors — the user just toggles until the arc lands correctly.
  const radiusPx = DOOR_RADIUS_M * scale;
  const doorArcs = doorPairs.map((p) => {
    const pos = Number.isFinite(p.position) ? p.position : 0.5;
    const centre = p.span_lo + pos * (p.span_hi - p.span_lo);
    const hingeOverride = p.hinge || "auto";        // auto | left | right
    const swingOverride = p.swing_direction || "auto"; // auto | inswing | outswing
    if (p.axis === "z") {
      // Auto swing follows the bigger-room heuristic.
      const autoSwingX = p.bigger.x > p.coord - 0.01 ? 1 : -1;
      const swingX = swingOverride === "inswing" ? 1
                   : swingOverride === "outswing" ? -1
                   : autoSwingX;
      // Hinge "right" pivots at the HIGH-Z end of the doorway, so the
      // closed-door direction flips along Z as well.
      const hingeAtHigh = hingeOverride === "right";
      const hingeWorldZ = hingeAtHigh
        ? centre + DOOR_RADIUS_M / 2
        : centre - DOOR_RADIUS_M / 2;
      const hingeX = toX(p.coord);
      const hingeY = toY(hingeWorldZ);
      const slabX = hingeX + swingX * radiusPx;
      const slabY = hingeY;
      const endX = hingeX;
      // Arc sweeps from slab (open 90°) back to the wall direction.
      // hingeAtHigh = true → arc goes DOWN-screen (toward LOW Z world,
      // which is toY=higher px). hingeAtHigh = false → up-screen.
      const endY = hingeY + (hingeAtHigh ? 1 : -1) * radiusPx;
      // SVG arc sweep flag depends on BOTH swing and hinge sides so the
      // arc actually traces between the slab end and the wall direction.
      const sweepFlag = (swingX > 0) === hingeAtHigh ? 1 : 0;
      const arcPath = `M ${slabX} ${slabY} A ${radiusPx} ${radiusPx} 0 0 ${sweepFlag} ${endX} ${endY}`;
      return {
        id: p.id, type: p.type || "swing",
        axis: "z", hingeX, hingeY, slabX, slabY, arcPath, swingX,
        hingeAtHigh,
        gapHalfX: 3, gapHalfY: radiusPx / 2,
        // Tag anchor — midpoint of the door opening, offset INTO the bigger room.
        tagX: hingeX + (swingX * radiusPx) / 2,
        tagY: hingeY + (hingeAtHigh ? -1 : 1) * radiusPx / 2,
      };
    }
    // axis === "x" — horizontal wall, doorway runs along X.
    const autoSwingY = p.bigger.z > p.coord - 0.01 ? -1 : 1;
    const swingY = swingOverride === "inswing" ? -1
                 : swingOverride === "outswing" ? 1
                 : autoSwingY;
    const hingeAtHigh = hingeOverride === "right";
    const hingeWorldX = hingeAtHigh
      ? centre + DOOR_RADIUS_M / 2
      : centre - DOOR_RADIUS_M / 2;
    const hingeX = toX(hingeWorldX);
    const hingeY = toY(p.coord);
    const slabX = hingeX + (hingeAtHigh ? -1 : 1) * radiusPx;
    const slabY = hingeY;
    const endX = hingeX;
    const endY = hingeY + swingY * radiusPx;
    const sweepFlag = ((hingeAtHigh ? -1 : 1) > 0) === (swingY > 0) ? 1 : 0;
    const arcPath = `M ${slabX} ${slabY} A ${radiusPx} ${radiusPx} 0 0 ${sweepFlag} ${endX} ${endY}`;
    return {
      id: p.id, type: p.type || "swing",
      axis: "x", hingeX, hingeY, slabX, slabY, arcPath, swingY,
      hingeAtHigh,
      gapHalfX: radiusPx / 2, gapHalfY: 3,
      tagX: hingeX + ((hingeAtHigh ? -1 : 1) * radiusPx) / 2,
      tagY: hingeY + (swingY * radiusPx) / 2,
    };
  });
  const windowSymbols = exteriorWindows.map((w) => ({
    id: w.id,
    type: w.type || "fixed",
    axis: w.axis === "x" ? "x" : "z",
    cx: w.axis === "x" ? toX(w.centre) : toX(w.coord),
    cy: w.axis === "x" ? toY(w.coord) : toY(w.centre),
    len: w.len,
  }));
  // Tiny dimension label helpers — extracted to /lib/floorplan2dHelpers.js
  // (V2.0.61). Both functions are imported at the top of this file.
  const isArch = planMode === "architectural";

  return (
    <div
      data-testid="floor-plan-2d"
      ref={containerRef}
      className={`relative h-full w-full overflow-auto bg-white ${className}`}
    >
      {/* V2.0.74 — Undo moved from bottom-left of the canvas to the TOP
          ribbon (right of the "+ Symbols" palette pill). Visibility is
          the whole point — you shouldn't have to scan a whole floor
          plan to find the "un-mess-this-up" button. Sits at top-4 left
          side, aligned horizontally with the Symbol palette. */}
      {rooms.length > 0 && (
        <button
          type="button"
          data-testid="floor-plan-undo-btn"
          onClick={undo}
          title="Undo (Ctrl/Cmd+Z)"
          className="absolute top-4 left-[130px] z-30 inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 font-mono text-[10px] uppercase tracking-widest shadow-sm transition-colors"
        >
          <Undo2 className="w-3 h-3" /> Undo
        </button>
      )}

      {/* V2.0.77 — Wall drawing tool. Toggle-button just to the right of
          Undo. When active, left-click on the canvas drops interior
          partition walls that chain (endpoint → next start) and persist
          into `floor.walls[]` with `user_drawn: true` so the AI knows
          to preserve them. Escape exits. */}
      {rooms.length > 0 && (
        <button
          type="button"
          data-testid="floor-plan-wall-tool-btn"
          onClick={wallTool.toggle}
          title="Draw interior walls (left-click to place, Escape to exit)"
          aria-pressed={wallTool.active}
          aria-label={wallTool.active ? "Exit wall drawing tool" : "Draw wall tool"}
          className={
            "absolute top-4 left-[210px] z-30 inline-flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[10px] uppercase tracking-widest shadow-sm transition-colors " +
            (wallTool.active
              ? "border-brass-700 bg-brass-600 text-white"
              : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50")
          }
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="2" y1="6" x2="10" y2="6" />
            <circle cx="2" cy="6" r="1.4" fill="currentColor" />
            <circle cx="10" cy="6" r="1.4" fill="currentColor" />
          </svg>
          {wallTool.active ? "// Drawing…" : "+ Wall"}
        </button>
      )}

      {/* V1.7.4 — Download Architectural PDF (paid, 1 credit). Top-center
          just below the canvas-view tabs so it reads as a "what to do with
          this plan" CTA. V1.9.1 — Draggable via the cyan grip handle on
          the left; position persists across reloads (localStorage). */}
      {designId && (
        <div
          /* V2.0.4 — hidden on phones; the chip is paywalled + large
             enough to push the canvas content around. Tablet+ keeps it. */
          className="absolute top-14 left-1/2 -translate-x-1/2 z-20 hidden sm:flex items-center"
          data-testid="preview-pdf-dock"
          {...previewPdfDrag.containerProps}
        >
          {/* Drag grip — pointer-down on this lets the user move the whole
              dock. Visual styling matches the button so it reads as part
              of the same chip. */}
          <span
            data-testid="preview-pdf-drag-handle"
            title="Drag to reposition"
            aria-label="Drag the Preview PDF button"
            className="inline-flex items-center justify-center w-5 h-7 border-r-0 border border-brass-700 bg-brass-50 text-brass-600 hover:bg-brass-100 select-none"
            {...previewPdfDrag.handleProps}
          >
            <svg width="8" height="14" viewBox="0 0 6 12" fill="currentColor">
              <circle cx="1.5" cy="1.5" r="1" />
              <circle cx="4.5" cy="1.5" r="1" />
              <circle cx="1.5" cy="6"   r="1" />
              <circle cx="4.5" cy="6"   r="1" />
              <circle cx="1.5" cy="10.5" r="1" />
              <circle cx="4.5" cy="10.5" r="1" />
            </svg>
          </span>
          <button
            type="button"
            data-testid="download-arch-pdf-btn"
            onClick={handlePreviewPDF}
            disabled={pdfState !== "idle" && pdfState !== "paywall"}
            className={
              "inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest shadow-sm transition-colors " +
              (pdfState === "previewing" || pdfState === "downloading"
                ? "border-zinc-300 bg-white text-zinc-400 cursor-wait"
                : "border-brass-700 bg-brass-50 text-brass-700 hover:bg-brass-100")
            }
          >
            {pdfState === "previewing" ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> Loading preview…
              </>
            ) : (
              <>
                <Eye className="w-3 h-3" /> // Preview Architectural PDF
              </>
            )}
          </button>
        </div>
      )}

      {/* V1.7.6 — Plan-canvas Compare Modes toggle (bottom-center). Free
          to flip between Diagram and Architectural styles — the value
          pitch is "see what the paid PDF looks like before you buy". */}
      {rooms.length > 0 && (
        <div
          data-testid="plan-mode-toggle"
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 inline-flex border border-zinc-300 bg-white shadow-sm"
        >
          <button
            type="button"
            data-testid="plan-mode-diagram"
            onClick={() => setPlanMode("diagram")}
            aria-pressed={planMode === "diagram"}
            className={
              "px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors " +
              (planMode === "diagram"
                ? "bg-brass-600 text-white"
                : "text-zinc-600 hover:bg-zinc-50")
            }
          >
            // Diagram
          </button>
          <button
            type="button"
            data-testid="plan-mode-architectural"
            onClick={() => setPlanMode("architectural")}
            aria-pressed={planMode === "architectural"}
            className={
              "px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border-l border-zinc-300 transition-colors " +
              (planMode === "architectural"
                ? "bg-zinc-900 text-white"
                : "text-zinc-600 hover:bg-zinc-50")
            }
          >
            // Architectural
          </button>
        </div>
      )}

      {/* V1.7.6 — Preview lightbox. Embeds the watermarked PDF directly
          so users see exactly what the paid version looks like, then
          unlock with one click. (V2.0.79: JSX extracted to ExportLightbox.jsx.) */}
      <ExportLightbox
        pdfState={pdfState}
        previewDataUrl={previewDataUrl}
        designId={designId}
        legalAccepted={legalAccepted}
        setLegalAcceptanceId={setLegalAcceptanceId}
        closeLightbox={closeLightbox}
        handleDownloadIFC={handleDownloadIFC}
        handleDownloadCleanPDF={handleDownloadCleanPDF}
      />

      {/* Paywall overlay — fires on HTTP 402 from /architectural-pdf */}
      {pdfState === "paywall" && (
        <ProjectUnlockPaywall
          designId={designId}
          intent="pdf"
          onClose={() => setPdfState("idle")}
          onUnlocked={() => {
            setPdfState("idle");
            // Retry the download immediately after a successful unlock.
            handleDownloadCleanPDF?.();
          }}
        />
      )}

      {/* Floor switcher (only when 2+ floors) */}
      {floors.length > 1 && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest">
          {floors.map((f) => {
            const idx = Number(f.floor_index ?? f.level);
            const active = idx === Number(activeFloor);
            return (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setActiveFloor(idx);
                  // V1.9.9 — bubble up so the parent's `floorIndex` prop
                  // (used by the 3D viewport + the persist writes) tracks
                  // the in-canvas selector.
                  onChangeFloor?.(idx);
                }}
                data-testid={`floor-plan-floor-btn-${idx}`}
                className={
                  "px-2 py-1 border " +
                  (active
                    ? "border-brass-600 bg-brass-50 text-brass-700"
                    : "border-zinc-300 text-zinc-500 hover:border-brass-500")
                }
              >
                FLOOR {idx}
              </button>
            );
          })}
        </div>
      )}

      {/* V1.8.7 — Door/Window count badge (top-right). Auto-shows when openings exist. */}
      {(doorArcs.length > 0 || windowSymbols.length > 0) && (
        <div
          data-testid="floor-plan-openings-badge"
          className={
            /* V2.0.4 — hidden on phones to free up canvas top-right; the
               counts are still discoverable via Code Check on mobile. */
            "absolute top-3 right-3 z-10 hidden sm:block font-mono text-[10px] uppercase tracking-widest px-2 py-1 border " +
            (isArch
              ? "bg-[#f5efe1]/95 border-zinc-400 text-zinc-700"
              : "bg-zinc-950/85 border-zinc-700 text-brass-300 backdrop-blur-md")
          }
        >
          // {doorArcs.length} {doorArcs.length === 1 ? "door" : "doors"} · {windowSymbols.length} {windowSymbols.length === 1 ? "window" : "windows"}
        </div>
      )}

      {/* V2.0.5 — pinch-zoom + 2-finger pan wrapper. CSS transform on a
          plain div preserves the SVG's intrinsic viewBox math (so all
          existing pointer-to-world conversions still work) while letting
          touch users zoom + pan independently of the auto-fit scale. The
          touch handlers live on the SVG so a single-finger touch still
          routes to the existing drag/click handlers. */}
      <div
        style={{
          transform: `translate(${touchZoom.tx}px, ${touchZoom.ty}px) scale(${touchZoom.scale})`,
          transformOrigin: "0 0",
          touchAction: touchZoom.scale === 1 ? "auto" : "none",
        }}
        className="origin-top-left"
        onWheel={handleSvgWheel}
        onMouseDown={handleSvgMouseDownPan}
        onContextMenu={(e) => {
          // Right-click is repurposed for pan in V2.0.61 — suppress the
          // browser context menu so the gesture feels natural.
          if (e.button === 2 || e.ctrlKey) e.preventDefault();
        }}
      >
      <svg
        ref={svgRef}
        width={svgW}
        height={svgH}
        viewBox={`0 0 ${svgW} ${svgH}`}
        /* V2.0.4 — `max-w-full h-auto` so the SVG can scale DOWN on
           viewports narrower than the intrinsic plan width (phones).
           The viewBox preserves coordinates so all overlay maths still
           lines up; the browser just scales the rendered pixels. */
        className="block mx-auto my-4 max-w-full h-auto"
        style={{ cursor: wallTool.active ? "crosshair" : (placeDrag.active ? "ew-resize" : (hoverWall ? "copy" : "default")) }}
        data-testid="floor-plan-2d-svg"
        onDragOver={handleSvgDragOver}
        onDrop={handleSvgDrop}
        onPointerMove={handleSvgPointerMove}
        onPointerUp={handleSvgPointerUp}
        onClick={handleSvgClick}
        onTouchStart={handleSvgTouchStart}
        onTouchMove={handleSvgTouchMove}
        onTouchEnd={handleSvgTouchEnd}
        onTouchCancel={handleSvgTouchEnd}
      >
        {/* Light grid for scale (1m squares) */}
        <defs>
          <pattern
            id="planGrid"
            width={scale}
            height={scale}
            patternUnits="userSpaceOnUse"
          >
            <path
              d={`M ${scale} 0 L 0 0 0 ${scale}`}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        {/* V1.8.8 — Hatch patterns for the 5 wall materials. Inlined here
            so the wall-segment <rect> fills below can reference them by id. */}
        <HatchDefs mode={isArch ? "architectural" : "diagram"} />
        <rect width={svgW} height={svgH} fill="white" />
        <rect
          x={PADDING}
          y={PADDING}
          width={innerW}
          height={innerH}
          fill="url(#planGrid)"
        />

        {/* Room rectangles */}
        {rooms.map((r) => {
          const x = toX(r.x);
          // After the Y-flip, the SVG "top" of the room corresponds to the
          // FAR edge of the rectangle (z + depth). Width stays in X; height
          // stays as the room's depth scaled.
          const y = toY(r.z + r.depth);
          const w = r.width * scale;
          const h = r.depth * scale;
          return (
            <g key={r.id} data-testid={`floor-plan-room-${r.id}`} data-mode={planMode}>
              <rect
                x={x}
                y={y}
                width={w}
                height={h}
                fill={isArch ? "#fafaf7" : "#f9fafb"}
                stroke="transparent"
                strokeWidth={0}
              />
              {/* Room name */}
              <text
                x={x + w / 2}
                y={y + h / 2 - 4}
                textAnchor="middle"
                className="font-mono"
                fontSize={Math.max(10, Math.min(14, w / 8))}
                fill="#111827"
                style={{ textTransform: "uppercase", letterSpacing: 1, fontWeight: isArch ? 700 : 500 }}
              >
                {(r.name || "ROOM").toUpperCase()}
              </text>
              {/* Area / dimensions (sqft in Diagram mode; feet-inches in Architectural) */}
              <text
                x={x + w / 2}
                y={y + h / 2 + 12}
                textAnchor="middle"
                className="font-mono"
                fontSize={10}
                fill="#6b7280"
              >
                {isArch
                  ? `${fmtFtIn(r.width)} x ${fmtFtIn(r.depth)}`
                  : (fmtSqft(r.area_sqft) || `${Math.round(r.width)}m × ${Math.round(r.depth)}m`)}
              </text>
            </g>
          );
        })}

        {/* V2.0.9 — Diagram mode walls. Rendered from computed
            wallSegments so merged-room shared edges (which are absent
            from the segment list) no longer paint a phantom wall. The
            old per-room rect-stroke approach drew the shared edge twice
            and made L-shaped open plans impossible. */}
        {!isArch && wallSegments.map((seg, i) => {
          if (seg.axis === "x") {
            return (
              <line
                key={`diag-wall-${i}`}
                x1={toX(seg.span_lo)} y1={toY(seg.coord)}
                x2={toX(seg.span_hi)} y2={toY(seg.coord)}
                stroke="#0041ba" strokeWidth={STROKE_INTERIOR}
              />
            );
          }
          return (
            <line
              key={`diag-wall-${i}`}
              x1={toX(seg.coord)} y1={toY(seg.span_lo)}
              x2={toX(seg.coord)} y2={toY(seg.span_hi)}
              stroke="#0041ba" strokeWidth={STROKE_INTERIOR}
            />
          );
        })}

        {/* V2.0.30 — Per-wall code-violation highlight overlay. Drawn
            on top of wall segments so the dashed coral-red callout sits
            above the standard wall stroke. Pointer-events disabled. */}
        <WallViolationLayer
          violations={violations}
          rooms={rooms}
          floorIndex={activeFloor}
          toX={toX}
          toY={toY}
        />

        {/* V2.0.82 — Per-wall test hitboxes. Invisible rects with a
            stable `data-testid` so Playwright E2E can precisely target
            a specific wall by id (e.g. `wall-hitbox-r1_north`). They
            have pointer-events="none" so real user clicks fall
            through to the SVG onClick handler unchanged — the existing
            `findWallAtPoint(rooms, x, z, 0.5)` code path is
            untouched. Test automation uses `page.locator('[data-testid=
            wall-hitbox-...]').click({force:true})` which dispatches at
            the rect's center, and that center lies exactly on the
            wall so findWallAtPoint hits. Renders in both isArch modes
            since it's a test-only surface. */}
        {wallSegments.map((seg, idx) => {
          // Uniqueness key: (axis, rounded coord + span). Segments are
          // guaranteed unique on that tuple within a single computed
          // set. Room ids embedded when available for readability, but
          // NOT relied on for uniqueness (some exterior walls have no
          // room_id — they'd otherwise collide).
          const roomTag = seg.room_id || seg.room_a || "";
          const roomTagB = seg.room_b || "";
          const coord = Math.round((seg.coord || 0) * 100);
          const lo = Math.round((seg.span_lo || 0) * 100);
          const hi = Math.round((seg.span_hi || 0) * 100);
          const geoKey = `${seg.axis}_${coord}_${lo}_${hi}`;
          const readable = [roomTag, roomTagB, seg.side || ""].filter(Boolean).join("_");
          const testId = readable
            ? `wall-hitbox-${readable}-${geoKey}`
            : `wall-hitbox-${geoKey}`;
          const HIT_PX = 6;
          if (seg.axis === "x") {
            const x = toX(seg.span_lo);
            const y = toY(seg.coord) - HIT_PX / 2;
            const w = Math.max(1, toX(seg.span_hi) - toX(seg.span_lo));
            return (
              <rect
                key={`hit-${geoKey}-${idx}`}
                data-testid={testId}
                data-wall-kind={seg.kind}
                x={x} y={y} width={w} height={HIT_PX}
                fill="transparent"
                pointerEvents="none"
              />
            );
          }
          const x = toX(seg.coord) - HIT_PX / 2;
          const y = toY(seg.span_hi);
          const h = Math.max(1, toY(seg.span_lo) - toY(seg.span_hi));
          return (
            <rect
              key={`hit-${geoKey}-${idx}`}
              data-testid={testId}
              data-wall-kind={seg.kind}
              x={x} y={y} width={HIT_PX} height={h}
              fill="transparent"
              pointerEvents="none"
            />
          );
        })}

        {/* V2.0.77 — User-drawn interior walls + wall-draw preview.
            (V2.0.79: JSX extracted to UserWallsLayer.jsx.) */}
        <UserWallsLayer
          floor={floor}
          toX={toX}
          toY={toY}
          scale={scale}
          wallTool={wallTool}
        />

        {/* V1.8.8 — Wall segments with material hatching. Only rendered in
            Architectural mode (Diagram keeps the simple blue stroke). Each
            segment is a thick band oriented along its axis, filled with the
            material's hatch pattern + outlined black. Wall tags (WT1, WT2 …)
            render at the segment midpoint with a leader to the schedule. */}
        {isArch && wallSegments.map((seg) => {
          const tPx = Math.max(3, seg.thickness_m * scale);
          const cat = MATERIAL_CATALOG[seg.material] || {};
          const accent = cat.color || "#1f2937";
          if (seg.axis === "x") {
            // Wall runs along x → horizontal band.
            const x0 = toX(seg.span_lo);
            const x1 = toX(seg.span_hi);
            const cy = toY(seg.coord);
            return (
              <g
                key={seg.id}
                data-testid={`floor-plan-wall-${seg.id}`}
                data-material={seg.material}
                data-kind={seg.kind}
              >
                <rect
                  x={x0}
                  y={cy - tPx / 2}
                  width={x1 - x0}
                  height={tPx}
                  fill="#ffffff"
                  stroke="#000"
                  strokeWidth={seg.kind === "exterior" ? 1.4 : 1.0}
                />
                <rect
                  x={x0}
                  y={cy - tPx / 2}
                  width={x1 - x0}
                  height={tPx}
                  fill={`url(#${cat.hatch})`}
                  stroke="none"
                  pointerEvents="none"
                />
              </g>
            );
          }
          // axis === 'z' → vertical band.
          const z0 = toY(seg.span_lo);
          const z1 = toY(seg.span_hi);
          const cx = toX(seg.coord);
          const yTop = Math.min(z0, z1);
          const yH = Math.abs(z1 - z0);
          return (
            <g
              key={seg.id}
              data-testid={`floor-plan-wall-${seg.id}`}
              data-material={seg.material}
              data-kind={seg.kind}
            >
              <rect
                x={cx - tPx / 2}
                y={yTop}
                width={tPx}
                height={yH}
                fill="#ffffff"
                stroke="#000"
                strokeWidth={seg.kind === "exterior" ? 1.4 : 1.0}
              />
              <rect
                x={cx - tPx / 2}
                y={yTop}
                width={tPx}
                height={yH}
                fill={`url(#${cat.hatch})`}
                stroke="none"
                pointerEvents="none"
              />
              <g style={{ display: "none" }} data-accent={accent} />
            </g>
          );
        })}

        {/* V1.8.8 — Wall tags. One amber-circled label per UNIQUE schedule
            entry, placed at the midpoint of the LONGEST segment of that tag,
            with a tiny leader pointing INTO the wall band. */}
        {isArch && wallSchedule.map((row) => {
          // Pick the longest segment for this tag for tag anchor.
          const matches = wallSegments.filter((s) => s.tag === row.tag);
          if (matches.length === 0) return null;
          const anchor = matches.reduce((best, s) => {
            const len = s.span_hi - s.span_lo;
            const bLen = best ? best.span_hi - best.span_lo : -1;
            return len > bLen ? s : best;
          }, null);
          if (!anchor) return null;
          const midSpan = (anchor.span_lo + anchor.span_hi) / 2;
          let tagX, tagY;
          if (anchor.axis === "x") {
            tagX = toX(midSpan);
            tagY = toY(anchor.coord) - 14;
          } else {
            tagX = toX(anchor.coord) + 14;
            tagY = toY(midSpan);
          }
          return (
            <g
              key={`tag-${row.tag}`}
              data-testid={`floor-plan-wall-tag-${row.tag}`}
              transform={`translate(${tagX}, ${tagY})`}
            >
              <rect x={-12} y={-7} width={24} height={14} fill="#fef3c7" stroke="#b45309" strokeWidth={1} />
              <text x="0" y="3" textAnchor="middle" fontSize="8" fontFamily="JetBrains Mono, monospace" fill="#7c2d12" fontWeight="700">
                {row.tag}
              </text>
            </g>
          );
        })}

        {/* V1.8.6 — Exterior window symbols. V1.8.7 — supports 4 architectural
            types per the wall-symbol legend:
              fixed       — single horizontal glass line
              double_hung — two parallel glass lines (overlapping panes)
              sliding     — three parallel lines (three panes)
              casement    — angled crank line projecting outward */}
        {windowSymbols.map((w, i) => {
          // V2.0.4 — Short display id for the canvas tag. Auto windows
          // carry stable identity ids like "auto-W-r1-rear-6.67"; the
          // diamond tag isn't wide enough to render those, so we use
          // the row index ("W1", "W2", ...) instead. Internal logic +
          // testids still use the stable id.
          const displayId = String(w.id || "").startsWith("auto-")
            ? `W${i + 1}`
            : w.id;
          const wallColor = isArch ? "#3f3f46" : "#fbbf24";
          const wallBg = isArch ? "#f5efe1" : "#0c0a09";
          const tagFill = isArch ? "#facc15" : "#0c0a09";
          const tagStroke = isArch ? "#92400e" : "#fbbf24";
          const tagText = isArch ? "#52525b" : "#fbbf24";
          if (w.axis === "x") {
            const x0 = w.cx - (w.len * scale) / 2;
            const x1 = w.cx + (w.len * scale) / 2;
            return (
              <g key={`win-${i}`} data-testid={`floor-plan-window-${i}`} data-opening-id={w.id || `idx-${i}`}>
                <g data-testid={`floor-plan-window-${w.id || `idx-${i}`}`}>
                {/* Glass-line break in the wall */}
                <rect
                  x={x0}
                  y={w.cy - 3}
                  width={w.len * scale}
                  height={6}
                  fill={wallBg}
                  stroke={wallColor}
                  strokeWidth={isArch ? 1.0 : 1.4}
                />
                {/* Type variations */}
                {(w.type === "double_hung" || w.type === "single_hung") && (
                  <>
                    <line x1={x0} y1={w.cy - 1} x2={x1} y2={w.cy - 1} stroke={wallColor} strokeWidth={0.7} />
                    <line x1={x0} y1={w.cy + 1} x2={x1} y2={w.cy + 1} stroke={wallColor} strokeWidth={0.7} />
                    {/* V2.0.1 — single-hung: only the BOTTOM sash is operable.
                        Mark the fixed (top) sash with a dashed accent. */}
                    {w.type === "single_hung" && (
                      <line x1={x0 + 2} y1={w.cy - 2.5} x2={x1 - 2} y2={w.cy - 2.5} stroke={wallColor} strokeWidth={0.5} strokeDasharray="1 1" />
                    )}
                  </>
                )}
                {w.type === "sliding" && (
                  <>
                    <line x1={x0} y1={w.cy - 1.5} x2={x1} y2={w.cy - 1.5} stroke={wallColor} strokeWidth={0.6} />
                    <line x1={x0} y1={w.cy}        x2={x1} y2={w.cy}        stroke={wallColor} strokeWidth={0.6} />
                    <line x1={x0} y1={w.cy + 1.5} x2={x1} y2={w.cy + 1.5} stroke={wallColor} strokeWidth={0.6} />
                  </>
                )}
                {w.type === "casement" && (
                  <>
                    <line x1={x0} y1={w.cy} x2={x1} y2={w.cy} stroke={wallColor} strokeWidth={0.7} />
                    {/* Crank line projecting outward (negative y = away from interior) */}
                    <line x1={(x0 + x1) / 2} y1={w.cy} x2={x1} y2={w.cy - 7} stroke={wallColor} strokeWidth={0.7} />
                  </>
                )}
                {w.type === "awning" && (
                  <>
                    {/* Awning: hinge at top, opens outward. Diagonal from
                        the TOP-LEFT to the BOTTOM-RIGHT corner of the
                        glass break. */}
                    <line x1={x0} y1={w.cy} x2={x1} y2={w.cy} stroke={wallColor} strokeWidth={0.7} />
                    <line x1={x0} y1={w.cy - 2} x2={x1} y2={w.cy + 2} stroke={wallColor} strokeWidth={0.6} />
                  </>
                )}
                {w.type === "hopper" && (
                  <>
                    {/* Hopper: hinge at bottom, opens inward. Diagonal
                        from the BOTTOM-LEFT to the TOP-RIGHT corner. */}
                    <line x1={x0} y1={w.cy} x2={x1} y2={w.cy} stroke={wallColor} strokeWidth={0.7} />
                    <line x1={x0} y1={w.cy + 2} x2={x1} y2={w.cy - 2} stroke={wallColor} strokeWidth={0.6} />
                  </>
                )}
                {w.type === "bay" && (
                  <>
                    {/* Bay: trapezoidal bump-out projecting outward. Outward
                        direction is -Y (away from the interior). */}
                    <line x1={x0} y1={w.cy} x2={x0 + 4} y2={w.cy - 6} stroke={wallColor} strokeWidth={0.9} />
                    <line x1={x0 + 4} y1={w.cy - 6} x2={x1 - 4} y2={w.cy - 6} stroke={wallColor} strokeWidth={0.9} />
                    <line x1={x1 - 4} y1={w.cy - 6} x2={x1} y2={w.cy} stroke={wallColor} strokeWidth={0.9} />
                  </>
                )}
                {(!w.type || w.type === "fixed") && (
                  <line x1={x0} y1={w.cy} x2={x1} y2={w.cy} stroke={wallColor} strokeWidth={0.8} />
                )}
                {/* Window TAG — diamond shape with the ID, per the user spec */}
                {w.id && w.len * scale >= 14 && (
                  <g transform={`translate(${w.cx}, ${w.cy + 10})`}>
                    <polygon points="0,-5 5,0 0,5 -5,0" fill={tagFill} stroke={tagStroke} strokeWidth={0.8} />
                    <text x="0" y="2" textAnchor="middle" fontSize="5.5" fontFamily="JetBrains Mono, monospace" fill={tagText} fontWeight="700">{displayId}</text>
                  </g>
                )}
                </g>
              </g>
            );
          }
          // Vertical window axis === 'z'
          const y0 = w.cy - (w.len * scale) / 2;
          const y1 = w.cy + (w.len * scale) / 2;
          return (
            <g key={`win-${i}`} data-testid={`floor-plan-window-${i}`} data-opening-id={w.id || `idx-${i}`}>
              <g data-testid={`floor-plan-window-${w.id || `idx-${i}`}`}>
              <rect
                x={w.cx - 3}
                y={y0}
                width={6}
                height={w.len * scale}
                fill={wallBg}
                stroke={wallColor}
                strokeWidth={isArch ? 1.0 : 1.4}
              />
              {(w.type === "double_hung" || w.type === "single_hung") && (
                <>
                  <line x1={w.cx - 1} y1={y0} x2={w.cx - 1} y2={y1} stroke={wallColor} strokeWidth={0.7} />
                  <line x1={w.cx + 1} y1={y0} x2={w.cx + 1} y2={y1} stroke={wallColor} strokeWidth={0.7} />
                  {w.type === "single_hung" && (
                    <line x1={w.cx - 2.5} y1={y0 + 2} x2={w.cx - 2.5} y2={y1 - 2} stroke={wallColor} strokeWidth={0.5} strokeDasharray="1 1" />
                  )}
                </>
              )}
              {w.type === "sliding" && (
                <>
                  <line x1={w.cx - 1.5} y1={y0} x2={w.cx - 1.5} y2={y1} stroke={wallColor} strokeWidth={0.6} />
                  <line x1={w.cx}        y1={y0} x2={w.cx}        y2={y1} stroke={wallColor} strokeWidth={0.6} />
                  <line x1={w.cx + 1.5} y1={y0} x2={w.cx + 1.5} y2={y1} stroke={wallColor} strokeWidth={0.6} />
                </>
              )}
              {w.type === "casement" && (
                <>
                  <line x1={w.cx} y1={y0} x2={w.cx} y2={y1} stroke={wallColor} strokeWidth={0.7} />
                  <line x1={w.cx} y1={(y0 + y1) / 2} x2={w.cx + 7} y2={y1} stroke={wallColor} strokeWidth={0.7} />
                </>
              )}
              {w.type === "awning" && (
                <>
                  <line x1={w.cx} y1={y0} x2={w.cx} y2={y1} stroke={wallColor} strokeWidth={0.7} />
                  <line x1={w.cx - 2} y1={y0} x2={w.cx + 2} y2={y1} stroke={wallColor} strokeWidth={0.6} />
                </>
              )}
              {w.type === "hopper" && (
                <>
                  <line x1={w.cx} y1={y0} x2={w.cx} y2={y1} stroke={wallColor} strokeWidth={0.7} />
                  <line x1={w.cx + 2} y1={y0} x2={w.cx - 2} y2={y1} stroke={wallColor} strokeWidth={0.6} />
                </>
              )}
              {w.type === "bay" && (
                <>
                  <line x1={w.cx} y1={y0} x2={w.cx - 6} y2={y0 + 4} stroke={wallColor} strokeWidth={0.9} />
                  <line x1={w.cx - 6} y1={y0 + 4} x2={w.cx - 6} y2={y1 - 4} stroke={wallColor} strokeWidth={0.9} />
                  <line x1={w.cx - 6} y1={y1 - 4} x2={w.cx} y2={y1} stroke={wallColor} strokeWidth={0.9} />
                </>
              )}
              {(!w.type || w.type === "fixed") && (
                <line x1={w.cx} y1={y0} x2={w.cx} y2={y1} stroke={wallColor} strokeWidth={0.8} />
              )}
              {w.id && w.len * scale >= 14 && (
                <g transform={`translate(${w.cx + 10}, ${w.cy})`}>
                  <polygon points="0,-5 5,0 0,5 -5,0" fill={tagFill} stroke={tagStroke} strokeWidth={0.8} />
                  <text x="0" y="2" textAnchor="middle" fontSize="5.5" fontFamily="JetBrains Mono, monospace" fill={tagText} fontWeight="700">{displayId}</text>
                </g>
              )}
              </g>
            </g>
          );
        })}

        {/* V1.8.6/7/V2.0.1 — Interior + exterior door symbols. Types:
              swing   — slab + quarter-arc swing (default)
              single  — same visual as swing (exterior single-leaf entry)
              french  — pair of mirrored swing arcs, both opening same direction
              dutch   — swing arc + horizontal divider across the slab
              sliding — two overlapping parallel lines inside the opening
              sliding_glass — wider sliding visual with framed glass panel
              pocket  — slab recedes into a wall-cavity rectangle
              bifold  — two V-shaped chevrons inside the opening */}
        {doorArcs.map((d, i) => {
          // V2.0.4 — Short display id for the canvas circle tag (see
          // window tag above). Auto doors carry stable ids like
          // "auto-D-r1-r5"; the circle tag is too small to render them.
          const displayId = String(d.id || "").startsWith("auto-")
            ? `D${i + 1}`
            : d.id;
          const wallBg = isArch ? "#f5efe1" : "#0c0a09";
          const slabColor = isArch ? "#1f2937" : "#d6ba6f";
          const arcColor = isArch ? "#52525b" : "#66522b";
          const tagFill = isArch ? "#fcd34d" : "#0c0a09";
          const tagStroke = isArch ? "#b45309" : "#d6ba6f";
          const tagText = isArch ? "#52525b" : "#d6ba6f";
          const openingW = d.axis === "z" ? d.gapHalfY * 2 : d.gapHalfX * 2;
          // V2.0.1 — derived helpers for the secondary-leaf renderers
          // (french). Mirror = pivot at the OPPOSITE end of the doorway.
          // V2.0.16 — make the type matcher tolerant. The AI sometimes
          // emits variants ("French", "french-doors", "sliding-glass",
          // "slider", "patio") so we normalise + match by prefix where
          // sensible. Previously these all fell through to the default
          // single swing leaf, which was why French + sliding-glass were
          // rendering as single doors after an AI turn.
          const _t = String(d.type || "").toLowerCase().trim().replace(/[\s_-]+/g, "_");
          const isFrench = _t === "french" || _t === "french_door" || _t === "french_doors" || _t === "double";
          const isSliding = _t === "sliding" || _t === "slider" || _t === "sliding_door" || _t === "sliding_doors";
          const isSlidingGlass = _t === "sliding_glass" || _t === "patio" || _t === "patio_door"
            || _t === "sliding_glass_door" || _t === "sliding_glass_doors" || _t.startsWith("sliding_glass");
          const isPocket = _t === "pocket" || _t === "pocket_door";
          const isBifold = _t === "bifold" || _t === "bi_fold" || _t === "bifold_door";
          const isSwing = !_t || _t === "swing" || _t === "single" || _t === "dutch"
            || (!isFrench && !isSliding && !isSlidingGlass && !isPocket && !isBifold);
          // Mirrored hinge offset along the wall axis.
          const mirrorDelta = d.axis === "z"
            ? { dx: 0, dy: (d.hingeAtHigh ? 1 : -1) * openingW }
            : { dx: (d.hingeAtHigh ? -1 : 1) * openingW, dy: 0 };
          const mirrorHingeX = d.hingeX + mirrorDelta.dx;
          const mirrorHingeY = d.hingeY + mirrorDelta.dy;
          const mirrorSlabX = mirrorHingeX + (d.slabX - d.hingeX);
          const mirrorSlabY = mirrorHingeY + (d.slabY - d.hingeY);
          // Mirrored arc end is along the wall axis from the mirror hinge,
          // in the OPPOSITE direction of the primary arc end.
          const mirrorEndX = mirrorHingeX - (d.hingeX === d.slabX ? 0 : 0);
          const mirrorEndY = mirrorHingeY - (d.axis === "z" ? (d.hingeY - mirrorHingeY) * -0 : 0);
          // Recompute end + sweep flag for the mirrored leaf using the
          // same formula as the primary arc but with flipped hinge end.
          const mirrorArc = (() => {
            const radiusPx2 = d.axis === "z" ? d.gapHalfY * 2 : d.gapHalfX * 2;
            if (d.axis === "z") {
              const flipDir = d.hingeAtHigh ? 1 : -1; // arc extends opposite direction
              const ey = mirrorHingeY - flipDir * radiusPx2;
              const sweep = (d.slabX > d.hingeX) === (flipDir < 0) ? 1 : 0;
              return `M ${mirrorSlabX} ${mirrorSlabY} A ${radiusPx2} ${radiusPx2} 0 0 ${sweep} ${mirrorHingeX} ${ey}`;
            }
            const flipDir = d.hingeAtHigh ? 1 : -1;
            const ex = mirrorHingeX - flipDir * radiusPx2;
            const sweep = (flipDir > 0) === (d.slabY > d.hingeY) ? 1 : 0;
            return `M ${mirrorSlabX} ${mirrorSlabY} A ${radiusPx2} ${radiusPx2} 0 0 ${sweep} ${ex} ${mirrorHingeY}`;
          })();
          // Mute unused-var lint (mirrorEnd* + mirrorArc placeholders).
          void mirrorEndX; void mirrorEndY;
          return (
            <g key={`door-${i}`} data-testid={`floor-plan-door-${i}`} data-door-type={d.type || "swing"} data-opening-id={d.id || `idx-${i}`}>
              <g data-testid={`floor-plan-door-${d.id || `idx-${i}`}`}>
              {/* Punch the wall line at the door location */}
              <rect
                x={d.hingeX - d.gapHalfX}
                y={d.hingeY - d.gapHalfY}
                width={d.gapHalfX * 2}
                height={d.gapHalfY * 2}
                fill={wallBg}
                stroke="none"
              />
              {(isSliding || isSlidingGlass) && (
                <g>
                  {/* sliding + sliding_glass share the parallel-line glyph;
                      sliding_glass adds a thin rectangle outline to read
                      as a framed glass panel. */}
                  {isSlidingGlass && (
                    <rect
                      x={d.axis === "z" ? d.hingeX - 3 : d.hingeX - openingW / 2}
                      y={d.axis === "z" ? d.hingeY - openingW / 2 : d.hingeY - 3}
                      width={d.axis === "z" ? 6 : openingW}
                      height={d.axis === "z" ? openingW : 6}
                      fill="none"
                      stroke={slabColor}
                      strokeWidth={0.6}
                    />
                  )}
                  {d.axis === "z" ? (
                    <>
                      <line x1={d.hingeX} y1={d.hingeY - openingW / 2}     x2={d.hingeX} y2={d.hingeY + openingW / 4} stroke={slabColor} strokeWidth={1.6} />
                      <line x1={d.hingeX + 1.5} y1={d.hingeY - openingW / 4} x2={d.hingeX + 1.5} y2={d.hingeY + openingW / 2} stroke={slabColor} strokeWidth={1.6} />
                    </>
                  ) : (
                    <>
                      <line x1={d.hingeX - openingW / 2} y1={d.hingeY}     x2={d.hingeX + openingW / 4} y2={d.hingeY} stroke={slabColor} strokeWidth={1.6} />
                      <line x1={d.hingeX - openingW / 4} y1={d.hingeY + 1.5} x2={d.hingeX + openingW / 2} y2={d.hingeY + 1.5} stroke={slabColor} strokeWidth={1.6} />
                    </>
                  )}
                </g>
              )}
              {isPocket && (
                <g>
                  {/* Wall-cavity rectangle (the "pocket") */}
                  <rect
                    x={d.axis === "z" ? d.hingeX - 4 : d.hingeX - openingW / 2}
                    y={d.axis === "z" ? d.hingeY - openingW / 2 : d.hingeY - 4}
                    width={d.axis === "z" ? 8 : openingW}
                    height={d.axis === "z" ? openingW : 8}
                    fill={wallBg}
                    stroke={slabColor}
                    strokeWidth={1.2}
                  />
                  {/* Slab line indicating recession direction */}
                  {d.axis === "z" ? (
                    <line x1={d.hingeX} y1={d.hingeY - openingW / 3} x2={d.hingeX} y2={d.hingeY + openingW / 3} stroke={slabColor} strokeWidth={1.4} />
                  ) : (
                    <line x1={d.hingeX - openingW / 3} y1={d.hingeY} x2={d.hingeX + openingW / 3} y2={d.hingeY} stroke={slabColor} strokeWidth={1.4} />
                  )}
                </g>
              )}
              {isBifold && (
                <g>
                  {/* Two chevrons inside the opening, pointing toward bigger room */}
                  {d.axis === "z" ? (
                    <>
                      <polyline points={`${d.hingeX},${d.hingeY - openingW / 2} ${d.hingeX + (d.swingX || 1) * 6},${d.hingeY - openingW / 4} ${d.hingeX},${d.hingeY}`} fill="none" stroke={slabColor} strokeWidth={1.3} />
                      <polyline points={`${d.hingeX},${d.hingeY} ${d.hingeX + (d.swingX || 1) * 6},${d.hingeY + openingW / 4} ${d.hingeX},${d.hingeY + openingW / 2}`} fill="none" stroke={slabColor} strokeWidth={1.3} />
                    </>
                  ) : (
                    <>
                      <polyline points={`${d.hingeX - openingW / 2},${d.hingeY} ${d.hingeX - openingW / 4},${d.hingeY + (d.swingY || 1) * 6} ${d.hingeX},${d.hingeY}`} fill="none" stroke={slabColor} strokeWidth={1.3} />
                      <polyline points={`${d.hingeX},${d.hingeY} ${d.hingeX + openingW / 4},${d.hingeY + (d.swingY || 1) * 6} ${d.hingeX + openingW / 2},${d.hingeY}`} fill="none" stroke={slabColor} strokeWidth={1.3} />
                    </>
                  )}
                </g>
              )}
              {isSwing && (
                <g>
                  <line x1={d.hingeX} y1={d.hingeY} x2={d.slabX} y2={d.slabY} stroke={slabColor} strokeWidth={isArch ? 1.6 : 1.4} />
                  <path d={d.arcPath} fill="none" stroke={arcColor} strokeWidth={0.9} strokeDasharray={isArch ? "0" : "3 2"} />
                  {/* V2.0.1 — Dutch: horizontal half-divider across the slab
                      so the door reads as split top/bottom. */}
                  {d.type === "dutch" && (
                    <line
                      x1={d.axis === "z" ? d.hingeX : (d.hingeX + d.slabX) / 2 - 4}
                      y1={d.axis === "z" ? (d.hingeY + d.slabY) / 2 - 4 : d.hingeY}
                      x2={d.axis === "z" ? d.slabX : (d.hingeX + d.slabX) / 2 + 4}
                      y2={d.axis === "z" ? (d.hingeY + d.slabY) / 2 + 4 : d.slabY}
                      stroke={slabColor}
                      strokeWidth={1.0}
                      strokeDasharray="2 2"
                    />
                  )}
                </g>
              )}
              {isFrench && (
                <g>
                  {/* Primary leaf */}
                  <line x1={d.hingeX} y1={d.hingeY} x2={d.slabX} y2={d.slabY} stroke={slabColor} strokeWidth={isArch ? 1.6 : 1.4} />
                  <path d={d.arcPath} fill="none" stroke={arcColor} strokeWidth={0.9} strokeDasharray={isArch ? "0" : "3 2"} />
                  {/* Mirrored leaf — same swing direction, hinge at the
                      opposite end of the doorway. */}
                  <line x1={mirrorHingeX} y1={mirrorHingeY} x2={mirrorSlabX} y2={mirrorSlabY} stroke={slabColor} strokeWidth={isArch ? 1.6 : 1.4} />
                  <path d={mirrorArc} fill="none" stroke={arcColor} strokeWidth={0.9} strokeDasharray={isArch ? "0" : "3 2"} />
                </g>
              )}
              {/* Door TAG — circle (per spec). Hidden for very small doors. */}
              {d.id && openingW >= 14 && (
                <g transform={`translate(${d.tagX}, ${d.tagY})`}>
                  <circle r="6" fill={tagFill} stroke={tagStroke} strokeWidth={0.8} />
                  <text x="0" y="2" textAnchor="middle" fontSize="5.5" fontFamily="JetBrains Mono, monospace" fill={tagText} fontWeight="700">{displayId}</text>
                </g>
              )}
              </g>
            </g>
          );
        })}

        {/* V1.8.3 — Fixtures (appliances + cabinetry) drawn as schematic
            rectangles with short uppercase labels. Loose representation
            only — the paid Architectural PDF gets exact measurements
            and proper architectural symbols. Skipped in 3D entirely. */}
        {fixtures.map((f) => {
          const style = getFixtureStyle(f.type);
          const x = toX(f.x);
          const y = toY(f.z + f.depth);
          const w = f.width * scale;
          const h = f.depth * scale;
          const cx = x + w / 2;
          const cy = y + h / 2;
          const labelSize = Math.max(7, Math.min(11, Math.sqrt(w * h) / 4));
          const isHovered =
            hoveredFixtureType &&
            String(f.type).toLowerCase() === hoveredFixtureType;
          return (
            <g
              key={f.id || `${f.type}-${f.x}-${f.z}`}
              data-testid={`floor-plan-fixture-${f.id || f.type}`}
            >
              {isHovered && (
                <rect
                  x={x - 4}
                  y={y - 4}
                  width={Math.max(0, w + 8)}
                  height={Math.max(0, h + 8)}
                  rx={4}
                  fill="none"
                  stroke="#facc15"
                  strokeWidth={2.5}
                  className="animate-pulse"
                />
              )}
              {style.shape === "circle" ? (
                <circle
                  cx={cx}
                  cy={cy}
                  r={Math.min(w, h) / 2 - 1}
                  fill={isArch ? style.fillA : style.fillD}
                  stroke={style.stroke}
                  strokeWidth={isArch ? 1.2 : 0.9}
                />
              ) : (
                <rect
                  x={x + 0.5}
                  y={y + 0.5}
                  width={Math.max(0, w - 1)}
                  height={Math.max(0, h - 1)}
                  fill={isArch ? style.fillA : style.fillD}
                  stroke={style.stroke}
                  strokeWidth={isArch ? 1.2 : 0.9}
                  strokeDasharray={isArch ? "0" : "3 2"}
                />
              )}
              {labelSize >= 8 && (
                <text
                  x={cx}
                  y={cy + labelSize / 3}
                  textAnchor="middle"
                  className="font-mono pointer-events-none"
                  fontSize={labelSize}
                  fill={style.stroke}
                  style={{ letterSpacing: 0.5, fontWeight: 600 }}
                >
                  {style.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Exterior outline — kept ONLY in diagram mode. In architectural
            mode the wall segments above already define the perimeter. */}
        {!isArch && (
          <rect
            x={PADDING}
            y={PADDING}
            width={innerW}
            height={innerH}
            fill="none"
            stroke="#000"
            strokeWidth={STROKE_EXTERIOR}
          />
        )}

        {/* Compass + scale bar at bottom-right */}
        <g transform={`translate(${svgW - 84}, ${svgH - 48})`}>
          <line x1="0" y1="0" x2={scale * 2} y2="0" stroke="#111827" strokeWidth="2" />
          <text
            x={scale}
            y="-4"
            textAnchor="middle"
            className="font-mono"
            fontSize="9"
            fill="#374151"
          >
            2 m
          </text>
          <g transform="translate(60, 8)">
            <circle r="14" fill="none" stroke="#111827" strokeWidth="1" />
            {/* V1.7 — top of plan = REAR of house; bottom = FRONT. Mark
                explicitly so users know which side faces the street. */}
            <text
              y="-4"
              textAnchor="middle"
              className="font-mono"
              fontSize="8"
              fill="#dc2626"
              fontWeight="bold"
            >
              REAR
            </text>
            <text
              y="14"
              textAnchor="middle"
              className="font-mono"
              fontSize="8"
              fill="#0041ba"
              fontWeight="bold"
            >
              FRONT
            </text>
            <line x1="0" y1="-10" x2="0" y2="10" stroke="#111827" strokeWidth="1" />
          </g>
        </g>

        {/* V1.9.2 — Invisible click overlay layer for doors + windows.
            Each rect spans the opening footprint at its actual world
            position so users can click anywhere on the symbol to open
            the type-editor popover. Only the EXPLICITLY-placed openings
            are editable (those with _explicitIdx >= 0); auto-inferred
            openings would need to be materialised first — for now we
            show a toast prompting the user to specify in chat. */}
        {/* V2.0.8 — Hover-preview wall highlight. Drawn under the
            opening overlays so existing doors/windows still get the
            click priority, but ABOVE the room fills so the user sees
            a clear cyan bar where their click will land. */}
        {hoverWall && !openingDrag && !placeDrag.active && (() => {
          const lo = hoverWall.span_lo;
          const hi = hoverWall.span_hi;
          if (hoverWall.axis === "x") {
            return (
              <line
                data-testid="wall-hover-preview"
                x1={toX(lo)} y1={toY(hoverWall.coord)}
                x2={toX(hi)} y2={toY(hoverWall.coord)}
                stroke={hoverWall.kind === "exterior" ? "#c4a96b" : "#a78bfa"}
                strokeWidth={5}
                strokeOpacity={0.55}
                pointerEvents="none"
              />
            );
          }
          return (
            <line
              data-testid="wall-hover-preview"
              x1={toX(hoverWall.coord)} y1={toY(lo)}
              x2={toX(hoverWall.coord)} y2={toY(hi)}
              stroke={hoverWall.kind === "exterior" ? "#c4a96b" : "#a78bfa"}
              strokeWidth={5}
              strokeOpacity={0.55}
              pointerEvents="none"
            />
          );
        })()}

        <OpeningOverlayLayer
          doorPairs={doorPairs}
          exteriorWindows={exteriorWindows}
          openingDrag={openingDrag}
          toX={toX}
          toY={toY}
          scale={scale}
          onOverlayPointerDown={handleOpeningPointerDown}
          onOverlayClick={handleOpeningClick}
        />

        {/* V1.9.2/V1.9.7 — Placed symbols layer (extracted into SymbolLayer). */}
        <SymbolLayer
          symbols={getCurrentSymbols()}
          scale={scale}
          toX={toX}
          toY={toY}
          selectedSymbolId={selectedSymbolId}
          moveSymbolId={moveSymbol?.id}
          measurementSystem={measurementSystem}
          onSymbolPointerDown={handleSymbolPointerDown}
          onResizeStart={handleResizeStart}
          fmtSize={fmtSize}
        />

        {/* V2.0.45 — Wall drag-resize handles (P1 Phase 2). Rendered
            last so the brass dots paint above openings and intercept
            pointer events before the opening overlays do. */}
        <WallHandleLayer
          floor={floor}
          floorIndex={activeFloor}
          blueprint={blueprint}
          scale={scale}
          toX={toX}
          toY={toY}
          onPreviewBlueprint={onPreviewBlueprint}
          onUpdateBlueprint={onUpdateBlueprint}
          enabled={WALL_HANDLES_ENABLED}
          adaMode={!!blueprint?.ada_mode}
        />
      </svg>
      </div>
      {/* V2.0.5 — pinch-zoom reset button. Only visible while the user
          has zoomed/panned via touch (scale != 1 or tx/ty != 0). Sits
          near the bottom-right above the chat FAB. */}
      {(touchZoom.scale !== 1 || touchZoom.tx !== 0 || touchZoom.ty !== 0) && (
        <button
          type="button"
          data-testid="floor-plan-zoom-reset-btn"
          onClick={resetTouchZoom}
          className="absolute bottom-16 right-3 z-30 inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 font-mono text-[10px] uppercase tracking-widest shadow-sm"
          title={`Zoom ×${touchZoom.scale.toFixed(2)} · click to reset`}
        >
          Reset Zoom · ×{touchZoom.scale.toFixed(1)}
        </button>
      )}
      {/* V2.0.61 — Pan/zoom hint that auto-fades after first interaction. */}
      {touchZoom.scale === 1 && touchZoom.tx === 0 && touchZoom.ty === 0 && (
        <div
          data-testid="floor-plan-panzoom-hint"
          className="absolute bottom-3 left-3 z-20 px-2 py-1 bg-white/85 backdrop-blur border border-zinc-200 text-zinc-500 font-mono text-[9px] uppercase tracking-widest pointer-events-none select-none hidden lg:block"
        >
          ⌘/Ctrl + scroll to zoom · right-drag to pan
        </div>
      )}
      <SymbolPalette onSymbolDragStart={(t) => setDragSymbol({ type: t })} />

      {/* V2.0.7 — Wall-click add-opening menu (desktop). Pops up when
          the user clicks a wall on the empty SVG background. Exterior
          walls offer Window/Door choice; interior walls go straight to
          the door-type picker. */}
      {wallAddMenu.menu && (
        <WallAddMenu
          wallKind={wallAddMenu.menu.wall.kind}
          anchorX={wallAddMenu.menu.anchorX}
          anchorY={wallAddMenu.menu.anchorY}
          recommendation={wallAddMenu.menu.recommendation}
          mergeOption={wallAddMenu.menu.mergeOption}
          onPick={wallAddMenu.handleAddPick}
          onMerge={wallAddMenu.handleMerge}
          onClose={wallAddMenu.close}
        />
      )}

      {/* V1.9.2 — Opening type editor (click a door/window to swap type). */}
      {openingEditor && (
        <OpeningEditor
          kind={openingEditor.kind}
          currentType={openingEditor.type}
          currentHinge={openingEditor.hinge}
          currentSwingDir={openingEditor.swing_direction}
          anchorX={openingEditor.anchorX}
          anchorY={openingEditor.anchorY}
          onChange={applyOpeningType}
          onHingeChange={(v) => applyOpeningField("hinge", v)}
          onSwingDirChange={(v) => applyOpeningField("swing_direction", v)}
          onDelete={deleteOpening}
          onClose={() => setOpeningEditor(null)}
        />
      )}

      {/* V2.0.74 — Material Schedule legend REMOVED. It was a static
          non-interactive legend showing symbol acronym meanings ("REF"
          = Refrigerator, etc.) but users couldn't drag or manipulate
          it, and the fixture labels on the plan itself already carry
          the same acronyms. Removing it declutters the canvas and
          eliminates the "why is this here?" confusion the user hit. */}
      {/* V1.8.8 — Wall Type Schedule (architectural mode only). */}
      {isArch && (
        <WallTypeLegend schedule={wallSchedule} isArch={isArch} />
      )}
    </div>
  );
}
